import { Card } from '@/components/design-system/Card';
import { Check } from 'lucide-react';
import { FormulaProduct, useGetFormulaBenefits } from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { Checkbox } from '@/components/design-system/Checkbox';
import { cn } from '@/utils/styles';
import { useNavigate } from 'react-router-dom';
import { constructFormulaBenefitsText } from '@screens/ShopPage/shopPageFunctions';
import { Skeleton } from '@design-system/Skeleton';
import EventTracker from '@utils/EventTracker';
import { analyticsEvent } from '@utils/analyticsEvent';

interface ProductCardProps {
  formula: FormulaProduct;
  isSelected?: boolean;
  onToggleSelected?: (productId: string) => void;
  isEditable?: boolean;
  cardStyle?: 'default' | 'subscribedHighlight';
}

export const ProductCard = ({
  formula,
  isSelected = true,
  onToggleSelected,
  isEditable = false,
  cardStyle = 'default',
}: ProductCardProps) => {
  const navigate = useNavigate();
  const { data: formulaBenefitsData, isLoading, isError } = useGetFormulaBenefits(
    Number(formula?.productId),
  );

  const handleCardClick = () => {
    if (!isEditable) {
      navigate(`/shop/product/${formula?.product_id}`);
    } else if (onToggleSelected) {
      onToggleSelected(String(formula.product_id));
    }

    // analytics tracking events
    if (formula?.productId === '1') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_MYFORMULAS_ORALLOZENGES);
    } else if (formula?.productId === '2') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_MYFORMULAS_SUPPLEMENTS);
    } else if (formula?.productId === '3') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_MYFORMULAS_GUTFORMULA);
    } else if (formula?.productId === '4') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_MYFORMULAS_TOOTHPASTEANDGEL);
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onToggleSelected) {
      onToggleSelected(String(formula.product_id));
    }
  };

  return (
    <Card
      className={cn(
        'mb-4 rounded-xl p-5 flex gap-4 transition-all cursor-pointer hover:shadow-lg relative justify-between',
        isEditable && isSelected && 'border-2 border-[#146464]',
        isEditable && !isSelected && 'border border-gray-300',
        !isEditable && 'border border-gray-200 hover:border-gray-400',
        cardStyle === 'subscribedHighlight' && 'bg-background-tertiary',
        cardStyle === 'default' && 'bg-white',
      )}
      onClick={handleCardClick}
    >
      <div className="flex flex-col items-start gap-4">
        {(!formula?.is_subscribed && formula?.is_retest_product) && (
          <div className="typography-body text-secondary bg-background-tertiary py-1 px-4 rounded w-fit mx-auto sm:mx-0">
            Now available with your latest test
          </div>
        )}
        <div className="flex">
          {formula.productImageUrl && (
            <div className="flex-shrink-0">
              <img
                src={formula.productImageUrl}
                alt={formula.title}
                className="h-12 w-12 object-contain"
              />
            </div>
          )}
          <div className="flex flex-col pl-2">
            <h3 className="typography-h3 text-primary font-medium mb-1">
              {formula.title}
            </h3>
            <div
              style={{ color: formula?.color }}
              className="typography-btn-medium font-serif italic mb-1"
            >
              {formula.ingredients_count} ingredients
            </div>
          </div>
        </div>
        {!isError && (
          <>
            {isLoading ? (
              <Skeleton className="h-12 bg-gray-100 rounded w-full z-50" />
            ) : (
              <p className="typography-body text-secondary">
                {constructFormulaBenefitsText(formulaBenefitsData)}
              </p>
            )}
          </>
        )}
      </div>

      <div>
        {isEditable ? (
          <Checkbox
            checked={isSelected}
            onClick={handleCheckboxClick}
            className="size-6 rounded-md border-2 data-[state=checked]:bg-[#146464] data-[state=unchecked]:border-gray-400 data-[state=checked]:border-[#146464]"
            aria-label={`Select ${formula.title}`}
          />
        ) : (
          cardStyle !== 'subscribedHighlight' &&
          isSelected && <Check className="w-5 h-5 text-[#009E82]" />
        )}
      </div>
    </Card>
  );
};
